import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, ArrowRight, Check, Users } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareOnX } from "@/components/ShareOnX";

const HERO = "https://images.unsplash.com/photo-1653991918209-f01269c0b695?auto=format&fit=crop&w=1600&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08 } }),
};

export const EVENT_TYPE_COLOR = {
  Rally: "bg-[#D90429] text-white",
  Training: "bg-[#0A1128] text-white",
  Workshop: "bg-[#F4A261] text-[#0A1128]",
  Community: "bg-slate-200 text-[#0A1128]",
  Summit: "bg-[#0A1128] text-white",
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [rsvps, setRsvps] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMember = user && user.role === "member";

  const loadEvents = () => api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  const loadRsvps = () => {
    if (!isMember) { setRsvps(new Set()); return; }
    api.get("/me/rsvps").then(({ data }) => setRsvps(new Set(data.map((e) => e.id)))).catch(() => {});
  };

  useEffect(() => { loadEvents(); }, []);
  useEffect(() => {
    loadRsvps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleRsvp = async (eventId) => {
    if (!user) { toast.info("Please log in to RSVP."); navigate("/login"); return; }
    if (user.role !== "member") { toast.info("RSVP is available to members."); return; }
    setBusy(eventId);
    const going = rsvps.has(eventId);
    try {
      if (going) {
        await api.delete(`/me/rsvps/${eventId}`);
        setRsvps((s) => { const n = new Set(s); n.delete(eventId); return n; });
        toast.success("RSVP canceled");
      } else {
        await api.post("/me/rsvps", { event_id: eventId });
        setRsvps((s) => new Set(s).add(eventId));
        toast.success("You're going! See you there.");
      }
      loadEvents();
    } catch {
      toast.error("Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Navbar />

      <PageHero
        image={HERO}
        imageAlt="Field of American flags"
        kicker="Get Involved"
        title={<>Upcoming <span className="text-[#D90429]">Events</span></>}
        subtitle="Rallies, trainings, and community actions across the nation. Show up, stand together, and be part of the mission."
      />

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 space-y-5">
          {events.length === 0 && <p className="text-slate-400 text-center py-12">No upcoming events right now — check back soon.</p>}
          {events.map((e, i) => (
            <motion.div
              key={e.id}
              initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              data-testid="event-card"
              className="border border-slate-200 hover:border-[#D90429] transition-colors duration-200 group overflow-hidden"
            >
              {e.image_file_id && (
                <div className="h-48 overflow-hidden bg-slate-100">
                  <img src={`${API}/public/files/${e.image_file_id}`} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              )}
              <div className="grid sm:grid-cols-[110px_1fr_auto] gap-6 items-center p-6">
                <div className="bg-[#0A1128] text-white text-center py-4 w-[100px]">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#F4A261]">{e.month}</div>
                  <div className="font-display text-4xl leading-none mt-1">{e.day}</div>
                </div>
                <div>
                  <span className={`inline-block px-2.5 py-1 text-xs uppercase tracking-wider font-semibold mb-3 ${EVENT_TYPE_COLOR[e.type] || "bg-slate-200 text-[#0A1128]"}`}>{e.type}</span>
                  <h3 className="font-display text-2xl uppercase leading-tight">{e.title}</h3>
                  <p className="text-slate-600 mt-2 leading-relaxed">{e.desc}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-slate-500">
                    <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#D90429]" /> {e.time}</span>
                    <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D90429]" /> {e.location}</span>
                    {e.rsvp_count > 0 && <span className="flex items-center gap-2" data-testid={`event-count-${e.id}`}><Users className="w-4 h-4 text-[#D90429]" /> {e.rsvp_count} going</span>}
                  </div>
                </div>
                <div className="justify-self-start sm:justify-self-end flex flex-col items-start sm:items-end gap-2.5">
                  <button
                    data-testid={`event-rsvp-button-${e.id}`}
                    onClick={() => toggleRsvp(e.id)}
                    disabled={busy === e.id}
                    className={`inline-flex items-center gap-2 px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold transition-colors duration-200 active:scale-95 disabled:opacity-60 ${
                      rsvps.has(e.id)
                        ? "bg-[#0A1128] text-white hover:bg-[#1a2547]"
                        : "bg-[#D90429] text-white hover:bg-[#b0031f]"
                    }`}
                  >
                    {rsvps.has(e.id) ? <><Check className="w-4 h-4" /> Going</> : <>RSVP <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <ShareOnX
                    testid={`share-on-x-${e.id}`}
                    text={`${e.title} — ${e.month} ${e.day} · ${e.location}. Join us! #PatriotPowerUSA`}
                    url={`${window.location.origin}/events`}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#0A1128] text-white py-20 grain stars relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <Calendar className="w-10 h-10 text-[#D90429] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Host An Event In Your Area</h2>
          <p className="text-white/70 mt-5 max-w-xl mx-auto">Members can organize local trainings and gatherings. Join to start a chapter near you.</p>
          <Link to="/join" data-testid="events-join-button" className="inline-flex items-center gap-2 mt-8 bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95">
            Join Our Newsletter <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
